import { useRef, useEffect, useState, useCallback } from 'react';
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

/**
 * Advanced background removal using MediaPipe Selfie Segmentation
 * Provides real-time person segmentation with customizable backgrounds
 */
export const useMediaPipeSegmentation = (options = {}) => {
  const {
    enabled = false,
    backgroundType = 'blur', // blur, color, image
    backgroundColor = '#0F172A',
    backgroundImageUrl = '',
    blurAmount = 10,
    modelSelection = 1, // 0 = general, 1 = landscape (faster)
    smoothSegmentation = true
  } = options;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const outputCanvasRef = useRef(null);
  const segmentationRef = useRef(null);
  const animationRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Load background image if needed
  useEffect(() => {
    if (backgroundType === 'image' && backgroundImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        backgroundImageRef.current = img;
      };
      img.onerror = () => {
        console.error('Failed to load background image');
      };
      img.src = backgroundImageUrl;
    }
  }, [backgroundType, backgroundImageUrl]);

  // Initialize MediaPipe Selfie Segmentation
  useEffect(() => {
    if (!enabled) return;

    const initSegmentation = async () => {
      try {
        const selfieSegmentation = new SelfieSegmentation({
          locateFile: (file) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
          }
        });

        selfieSegmentation.setOptions({
          modelSelection: modelSelection,
          selfieMode: true,
        });

        selfieSegmentation.onResults(onResults);

        await selfieSegmentation.initialize();
        segmentationRef.current = selfieSegmentation;
        setIsReady(true);
        setError(null);
      } catch (err) {
        console.error('MediaPipe initialization error:', err);
        setError(err.message);
        setIsReady(false);
      }
    };

    initSegmentation();

    return () => {
      if (segmentationRef.current) {
        segmentationRef.current.close();
        segmentationRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setIsReady(false);
    };
  }, [enabled, modelSelection]);

  // Process segmentation results
  const onResults = useCallback((results) => {
    if (!canvasRef.current || !outputCanvasRef.current) return;

    const canvas = canvasRef.current;
    const outputCanvas = outputCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const outputCtx = outputCanvas.getContext('2d');

    const width = results.image.width;
    const height = results.image.height;

    canvas.width = width;
    canvas.height = height;
    outputCanvas.width = width;
    outputCanvas.height = height;

    // Save context state
    outputCtx.save();

    // Clear canvas
    outputCtx.clearRect(0, 0, width, height);

    // Draw background based on type
    if (backgroundType === 'blur') {
      // Draw blurred background
      outputCtx.filter = `blur(${blurAmount}px)`;
      outputCtx.drawImage(results.image, 0, 0, width, height);
      outputCtx.filter = 'none';
    } else if (backgroundType === 'color') {
      // Solid color background
      outputCtx.fillStyle = backgroundColor;
      outputCtx.fillRect(0, 0, width, height);
    } else if (backgroundType === 'image' && backgroundImageRef.current) {
      // Custom image background
      const img = backgroundImageRef.current;
      const scale = Math.max(width / img.width, height / img.height);
      const x = (width - img.width * scale) / 2;
      const y = (height - img.height * scale) / 2;
      outputCtx.drawImage(img, x, y, img.width * scale, img.height * scale);
    } else {
      // Default: transparent/black
      outputCtx.fillStyle = '#000000';
      outputCtx.fillRect(0, 0, width, height);
    }

    // Apply segmentation mask
    outputCtx.globalCompositeOperation = 'destination-out';
    outputCtx.drawImage(results.segmentationMask, 0, 0, width, height);

    // Draw original image where person is
    outputCtx.globalCompositeOperation = 'destination-over';
    outputCtx.drawImage(results.image, 0, 0, width, height);

    // Restore context
    outputCtx.restore();

  }, [backgroundType, backgroundColor, blurAmount]);

  // Process video frame
  const processFrame = useCallback(async () => {
    if (!videoRef.current || !segmentationRef.current || !enabled || !isReady) {
      return;
    }

    const video = videoRef.current;

    if (video.readyState >= 2) {
      try {
        await segmentationRef.current.send({ image: video });
      } catch (err) {
        console.error('Segmentation error:', err);
      }
    }

    animationRef.current = requestAnimationFrame(processFrame);
  }, [enabled, isReady]);

  // Start processing
  const startProcessing = useCallback((video) => {
    videoRef.current = video;
    setIsProcessing(true);
    
    if (isReady) {
      processFrame();
    }
  }, [isReady, processFrame]);

  // Stop processing
  const stopProcessing = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsProcessing(false);
  }, []);

  // Start processing when ready
  useEffect(() => {
    if (isReady && isProcessing && videoRef.current) {
      processFrame();
    }
  }, [isReady, isProcessing, processFrame]);

  // Get processed stream from canvas
  const getProcessedStream = useCallback((fps = 30) => {
    if (!outputCanvasRef.current) return null;
    try {
      return outputCanvasRef.current.captureStream(fps);
    } catch (err) {
      console.error('Failed to capture stream:', err);
      return null;
    }
  }, []);

  return {
    canvasRef,
    outputCanvasRef,
    isReady,
    isProcessing,
    error,
    startProcessing,
    stopProcessing,
    getProcessedStream
  };
};

/**
 * Video effects processor for recording
 * Handles zoom, cursor highlight, click effects, and other visual enhancements
 */
export const useVideoEffectsProcessor = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [clicks, setClicks] = useState([]);
  const clickTimeoutRefs = useRef([]);

  const [effects, setEffects] = useState({
    zoom: { 
      enabled: false, 
      level: 1.5, 
      x: 0.5, 
      y: 0.5,
      smooth: true,
      followMouse: false
    },
    cursor: { 
      highlight: true, 
      highlightColor: 'rgba(59, 130, 246, 0.3)',
      highlightSize: 30,
      clickEffect: true,
      clickColor: 'rgba(239, 68, 68, 0.5)',
      clickSize: 40,
      clickDuration: 300
    },
    spotlight: {
      enabled: false,
      size: 200,
      blur: 50
    }
  });

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleClick = (e) => {
      if (effects.cursor.clickEffect) {
        const clickId = Date.now();
        setClicks(prev => [...prev, { id: clickId, x: e.clientX, y: e.clientY }]);
        
        // Remove click after animation
        const timeout = setTimeout(() => {
          setClicks(prev => prev.filter(c => c.id !== clickId));
        }, effects.cursor.clickDuration);
        
        clickTimeoutRefs.current.push(timeout);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      clickTimeoutRefs.current.forEach(t => clearTimeout(t));
    };
  }, [effects.cursor.clickEffect, effects.cursor.clickDuration]);

  // Toggle zoom
  const toggleZoom = useCallback((customLevel) => {
    setEffects(prev => ({
      ...prev,
      zoom: { 
        ...prev.zoom, 
        enabled: !prev.zoom.enabled,
        level: customLevel || prev.zoom.level
      }
    }));
  }, []);

  // Set zoom level
  const setZoomLevel = useCallback((level) => {
    setEffects(prev => ({
      ...prev,
      zoom: { ...prev.zoom, level }
    }));
  }, []);

  // Toggle zoom follow mouse
  const toggleZoomFollowMouse = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      zoom: { ...prev.zoom, followMouse: !prev.zoom.followMouse }
    }));
  }, []);

  // Toggle cursor highlight
  const toggleCursorHighlight = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      cursor: { ...prev.cursor, highlight: !prev.cursor.highlight }
    }));
  }, []);

  // Set cursor highlight color
  const setCursorHighlightColor = useCallback((color) => {
    setEffects(prev => ({
      ...prev,
      cursor: { ...prev.cursor, highlightColor: color }
    }));
  }, []);

  // Set cursor highlight size
  const setCursorHighlightSize = useCallback((size) => {
    setEffects(prev => ({
      ...prev,
      cursor: { ...prev.cursor, highlightSize: size }
    }));
  }, []);

  // Toggle click effect
  const toggleClickEffect = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      cursor: { ...prev.cursor, clickEffect: !prev.cursor.clickEffect }
    }));
  }, []);

  // Toggle spotlight mode
  const toggleSpotlight = useCallback(() => {
    setEffects(prev => ({
      ...prev,
      spotlight: { ...prev.spotlight, enabled: !prev.spotlight.enabled }
    }));
  }, []);

  // Apply effects to a video frame
  const applyEffects = useCallback((sourceCanvas, destCanvas, screenBounds) => {
    if (!sourceCanvas || !destCanvas) return;

    const ctx = destCanvas.getContext('2d');
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;

    destCanvas.width = width;
    destCanvas.height = height;

    ctx.save();

    // Apply zoom if enabled
    if (effects.zoom.enabled && effects.zoom.level > 1) {
      const zoomLevel = effects.zoom.level;
      let zoomX = effects.zoom.x;
      let zoomY = effects.zoom.y;

      // Follow mouse if enabled
      if (effects.zoom.followMouse && screenBounds) {
        zoomX = (mousePosition.x - screenBounds.left) / screenBounds.width;
        zoomY = (mousePosition.y - screenBounds.top) / screenBounds.height;
      }

      const zoomedWidth = width / zoomLevel;
      const zoomedHeight = height / zoomLevel;
      const sx = Math.max(0, Math.min(width - zoomedWidth, zoomX * width - zoomedWidth / 2));
      const sy = Math.max(0, Math.min(height - zoomedHeight, zoomY * height - zoomedHeight / 2));

      ctx.drawImage(sourceCanvas, sx, sy, zoomedWidth, zoomedHeight, 0, 0, width, height);
    } else {
      ctx.drawImage(sourceCanvas, 0, 0);
    }

    // Apply spotlight effect
    if (effects.spotlight.enabled && screenBounds) {
      const mx = ((mousePosition.x - screenBounds.left) / screenBounds.width) * width;
      const my = ((mousePosition.y - screenBounds.top) / screenBounds.height) * height;

      // Create dark overlay with spotlight cutout
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, width, height);

      ctx.globalCompositeOperation = 'destination-out';
      const gradient = ctx.createRadialGradient(mx, my, 0, mx, my, effects.spotlight.size);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.8)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(mx, my, effects.spotlight.size, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = 'source-over';
    }

    // Draw cursor highlight
    if (effects.cursor.highlight && screenBounds) {
      const mx = ((mousePosition.x - screenBounds.left) / screenBounds.width) * width;
      const my = ((mousePosition.y - screenBounds.top) / screenBounds.height) * height;

      ctx.beginPath();
      ctx.arc(mx, my, effects.cursor.highlightSize, 0, Math.PI * 2);
      ctx.fillStyle = effects.cursor.highlightColor;
      ctx.fill();
    }

    // Draw click effects
    if (effects.cursor.clickEffect && screenBounds) {
      clicks.forEach(click => {
        const cx = ((click.x - screenBounds.left) / screenBounds.width) * width;
        const cy = ((click.y - screenBounds.top) / screenBounds.height) * height;

        // Ripple effect
        ctx.beginPath();
        ctx.arc(cx, cy, effects.cursor.clickSize, 0, Math.PI * 2);
        ctx.strokeStyle = effects.cursor.clickColor;
        ctx.lineWidth = 3;
        ctx.stroke();

        // Inner circle
        ctx.beginPath();
        ctx.arc(cx, cy, effects.cursor.clickSize / 2, 0, Math.PI * 2);
        ctx.fillStyle = effects.cursor.clickColor;
        ctx.fill();
      });
    }

    ctx.restore();
  }, [effects, mousePosition, clicks]);

  return {
    canvasRef,
    effects,
    mousePosition,
    clicks,
    toggleZoom,
    setZoomLevel,
    toggleZoomFollowMouse,
    toggleCursorHighlight,
    setCursorHighlightColor,
    setCursorHighlightSize,
    toggleClickEffect,
    toggleSpotlight,
    applyEffects,
    setEffects
  };
};

// Re-export simple hooks for backward compatibility
export { useBackgroundRemoval, useVideoEffects } from './useVideoProcessing';

export default useMediaPipeSegmentation;
